// src/modules/gerenciador/CadetesDashboard.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { db, paths, auth } from '../../firebase/config';
import { 
    collection, 
    query, 
    where, 
    getDocs, 
    onSnapshot, 
    doc, 
    setDoc, 
    deleteDoc, 
    updateDoc, 
    getDoc 
} from "firebase/firestore";
// Imports para Auth 
import { initializeApp, getApp } from "firebase/app";
import { getAuth, createUserWithEmailAndPassword, signOut, signInWithEmailAndPassword } from "firebase/auth";
import { useAuth, logAuditoria } from '../../context/AuthContext';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { Button } from '../../components/Button';
import { Modal } from '../../components/Modal';

// --- Ícones ---
const UserIcon = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>;
const EditIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>;
const TrashIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>;
const PlusIcon = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>;
const SearchIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>;
const UploadIcon = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>;
const KeyIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg>;

export const CadetesDashboard = () => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  
  // Dados
  const [cadetes, setCadetes] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [companhias, setCompanhias] = useState([]);
  const [pelotoes, setPelotoes] = useState([]);

  // Filtros
  const [activeTurmaId, setActiveTurmaId] = useState('');
  const [activeCiaId, setActiveCiaId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(""); 

  // Ref para input de arquivo
  const fileInputRef = useRef(null);

  // Estado Inicial do Formulário
  const initialFormState = {
      turma_id: '',
      companhia_id: '',
      pelotao_id: '',
      nivel: 'cadete', 
      qra: '',          
      nome: '',         
      cpf: '',
      re: '',
      senha: '', // Campo visual da senha
      data_nascimento: '',
      sexo: 'M',
      estado_civil: '', 
      origem_ingresso: '',
      vinculo: 'PM',
      data_posse: '',
      antiguidade_concurso: '',
      antiguidade_1cfo: '',
      antiguidade_2cfo: '',
      antiguidade_3cfo: '',
      antiguidade_4cfo: '',
      acesso_liberado: true,
      isLiderEO: false, // --- CAMPO NOVO ADICIONADO ---
      canPostLivroDeOrdem: false
  };
  const [formData, setFormData] = useState(initialFormState);

  // Carregamento de Dados
  useEffect(() => {
    setLoading(true);
    
    const unsubTurmas = onSnapshot(collection(db, paths.turmas), (s) => {
        const list = s.docs.map(d => ({id: d.id, ...d.data()})).sort((a,b)=>a.nome.localeCompare(b.nome));
        setTurmas(list);
        if(list.length > 0 && !activeTurmaId) setActiveTurmaId(list[0].id);
    });
    const unsubCias = onSnapshot(collection(db, paths.companhias), (s) => setCompanhias(s.docs.map(d => ({id: d.id, ...d.data()})).sort((a,b)=>a.nome.localeCompare(b.nome))));
    const unsubPelotoes = onSnapshot(collection(db, paths.pelotoes), (s) => setPelotoes(s.docs.map(d => ({id: d.id, ...d.data()})).sort((a,b)=>a.nome.localeCompare(b.nome))));

    // --- CARREGAMENTO DE USUÁRIOS ---
    const qCadetes = query(collection(db, paths.users));
    const unsubCadetes = onSnapshot(qCadetes, (s) => {
        const list = s.docs.map(d => ({ id: d.id, ...d.data() }))
            .filter(u => {
                const nivelNormalizado = (u.nivel || '').toLowerCase().trim();
                const temNivelValido = ['cadete', 'cadete_academico', 'escalante', 'aluno', 'aspirante'].includes(nivelNormalizado);
                const temPelotao = u.pelotao_id && u.pelotao_id.trim() !== '';
                const temTurma = u.turma_id && u.turma_id.trim() !== '';

                return temNivelValido || temPelotao || temTurma;
            })
            .sort((a, b) => {
                const antA = parseInt(a.ordem_antiguidade || 9999);
                const antB = parseInt(b.ordem_antiguidade || 9999);
                if (antA !== antB) return antA - antB;
                return (a.nome || '').localeCompare(b.nome || '');
            });
        setCadetes(list);
        setLoading(false);
    });

    return () => { unsubTurmas(); unsubCias(); unsubPelotoes(); unsubCadetes(); };
  }, []);

  // --- Filtros Dinâmicos ---
  const filteredCias = useMemo(() => {
      if (!activeTurmaId) return [];
      return companhias.filter(c => c.turma_id === activeTurmaId);
  }, [companhias, activeTurmaId]);

  const displayCadetes = useMemo(() => {
      let list = cadetes;
      
      if (searchTerm) {
          const lower = searchTerm.toLowerCase();
          return list.filter(c => 
              (c.nome && c.nome.toLowerCase().includes(lower)) || 
              (c.qra && c.qra.toLowerCase().includes(lower)) ||
              (c.nome_guerra && c.nome_guerra.toLowerCase().includes(lower)) ||
              (c.re && c.re.includes(lower)) ||
              (c.matricula && c.matricula.includes(lower)) ||
              (c.cpf && c.cpf.includes(lower))
          );
      }

      if (activeTurmaId) list = list.filter(c => c.turma_id === activeTurmaId);
      if (activeCiaId) {
          const pelotaoIdsDaCia = pelotoes.filter(p => p.companhia_id === activeCiaId).map(p => p.id);
          list = list.filter(c => pelotaoIdsDaCia.includes(c.pelotao_id) || c.companhia_id === activeCiaId);
      }
      
      return list;
  }, [cadetes, activeTurmaId, activeCiaId, searchTerm, pelotoes]);

  const handleOpenModal = (cadete = null) => {
      if (cadete) {
          setIsEditing(true);
          setCurrentId(cadete.id);
          setFormData({
              turma_id: cadete.turma_id || activeTurmaId || '', 
              nivel: cadete.nivel || 'cadete',
              qra: cadete.qra || cadete.nome_guerra || cadete.nome || '',
              nome: cadete.nome || '',
              cpf: cadete.cpf || '',
              re: cadete.re || cadete.matricula || '',
              senha: cadete.senha || cadete.cpf || '', 
              data_nascimento: cadete.data_nascimento || '',
              sexo: cadete.sexo || 'M',
              estado_civil: cadete.estado_civil || '',
              origem_ingresso: cadete.origem_ingresso || '',
              vinculo: cadete.vinculo || 'PM',
              data_posse: cadete.data_posse || '',
              antiguidade_concurso: cadete.antiguidade_concurso || '',
              antiguidade_1cfo: cadete.antiguidade_1cfo || '',
              antiguidade_2cfo: cadete.antiguidade_2cfo || '',
              antiguidade_3cfo: cadete.antiguidade_3cfo || '',
              antiguidade_4cfo: cadete.antiguidade_4cfo || '',
              acesso_liberado: cadete.acesso_liberado !== false,
              isLiderEO: cadete.isLiderEO || false, // --- CARREGA O VALOR AO EDITAR ---
              canPostLivroDeOrdem: cadete.canPostLivroDeOrdem || false
          });
      } else {
          setIsEditing(false);
          setCurrentId(null);
          setFormData({ ...initialFormState, turma_id: activeTurmaId });
      }
      setShowModal(true);
  };

  // --- LÓGICA DO BOTÃO RESETAR SENHA ---
  const handleResetSenhaParaCPF = async () => {
      if (!currentId || !formData.cpf) {
          alert("Erro: Cadastro sem ID ou CPF.");
          return;
      }
      const cpfLimpo = formData.cpf.replace(/\D/g, '');
      const confirm = window.confirm(`ATENÇÃO: Você está resetando a senha do usuário CPF: ${cpfLimpo}, isso fará com que a senha se torne a mesma que o CPF.\n\nDeseja continuar?`);

      if (!confirm) return;

      setProcessing(true);
      try {
          await updateDoc(doc(db, paths.users, currentId), {
              senha: cpfLimpo, 
              atualizado_em: new Date().toISOString()
          });
          setFormData(prev => ({ ...prev, senha: cpfLimpo }));
          await logAuditoria(userProfile, "Resetou Senha (BD)", { id: currentId, nome: formData.nome });
          alert(`A senha do CPF ${cpfLimpo} foi resetada com sucesso.`);
      } catch (error) {
          console.error("Erro ao atualizar banco:", error);
          alert("Erro ao atualizar o registro: " + error.message);
      } finally {
          setProcessing(false);
      }
  };

 const handleSave = async (e) => {
      e.preventDefault();
      setProcessing(true);

      try {
          const turmaObj = turmas.find(t => t.id === formData.turma_id);
          const cpfLimpo = formData.cpf.replace(/\D/g, '');
          
          const dataToSave = {
              nome: formData.nome.toUpperCase(),
              qra: formData.qra.toUpperCase(), 
              nome_guerra: formData.qra.toUpperCase(),
              re: formData.re,
              matricula: formData.re,
              cpf: cpfLimpo, 
              senha: formData.senha || cpfLimpo, 
              data_nascimento: formData.data_nascimento,
              sexo: formData.sexo,
              estado_civil: formData.estado_civil,
              origem_ingresso: formData.origem_ingresso,
              vinculo: formData.vinculo,
              data_posse: formData.data_posse,
              nivel: formData.nivel,
              antiguidade_concurso: formData.antiguidade_concurso,
              antiguidade_1cfo: formData.antiguidade_1cfo,
              antiguidade_2cfo: formData.antiguidade_2cfo,
              antiguidade_3cfo: formData.antiguidade_3cfo,
              antiguidade_4cfo: formData.antiguidade_4cfo,
              ordem_antiguidade: formData.antiguidade_4cfo || formData.antiguidade_3cfo || formData.antiguidade_2cfo || formData.antiguidade_1cfo || formData.antiguidade_concurso,
              acesso_liberado: formData.acesso_liberado,
              turma_id: formData.turma_id,
              turma_nome: turmaObj?.nome || '',
              isLiderEO: formData.isLiderEO, // --- SALVA O VALOR NO BD ---
              canPostLivroDeOrdem: formData.canPostLivroDeOrdem || false,
                atualizado_em: new Date().toISOString()
          };

          if (isEditing) {
              const docRef = doc(db, paths.users, currentId);
              const docSnap = await getDoc(docRef);
              
              if (docSnap.exists()) {
                  const currentData = docSnap.data();
                  dataToSave.pelotao_id = currentData.pelotao_id || '';
                  dataToSave.pelotao_nome = currentData.pelotao_nome || '';
                  dataToSave.companhia_id = currentData.companhia_id || '';
                  delete dataToSave.posto;
                  delete dataToSave.funcao;

                  await updateDoc(docRef, dataToSave);
                  await logAuditoria(userProfile, "Atualizou Cadete", { id: currentId, nome: dataToSave.nome });
                  alert("Dados do Cadete atualizados com sucesso.");
              }
          } else {
              if (!formData.cpf) throw new Error("CPF é obrigatório para gerar o login.");
              
                const cpfLimpo = formData.cpf.replace(/\D/g, '');
                const generatedEmail = `${cpfLimpo}@sistema.com`; // Adicione o @dominio aqui
                const generatedPassword = cpfLimpo;

              let secondaryApp;
              try {
                  secondaryApp = getApp("Secondary");
              } catch (e) {
                  secondaryApp = initializeApp(auth.app.options, "Secondary");
              }
              const secondaryAuth = getAuth(secondaryApp);
              
              try {
                  const userCredential = await createUserWithEmailAndPassword(secondaryAuth, generatedEmail, generatedPassword);
                  const uid = userCredential.user.uid;
                  await signOut(secondaryAuth);
                  
                  dataToSave.perfil = dataToSave.nivel;
                  dataToSave.pelotao_id = '';
                  dataToSave.pelotao_nome = '';

                  await setDoc(doc(db, paths.users, uid), {
                      id: uid,
                      email: generatedEmail,
                      criado_em: new Date().toISOString(),
                      ...dataToSave
                  });

                  await logAuditoria(userProfile, "Criou Novo Cadete", { id: uid, nome: dataToSave.nome });
                  alert(`Cadete cadastrado com sucesso!\n\nLOGIN GERADO:\nE-mail: ${generatedEmail}\nSenha: ${generatedPassword}`);
                  
              } catch (authErr) {
                  if (authErr.code === 'auth/email-already-in-use') {
                      alert("ERRO: Este CPF já possui um login cadastrado no sistema.");
                  } else {
                      console.error(authErr);
                      throw new Error("Erro ao criar login: " + authErr.message);
                  }
              }
          }
          setShowModal(false);
      } catch (error) {
          console.error(error);
          alert("Erro ao salvar: " + error.message);
      } finally {
          setProcessing(false);
      }
  };

  const handleDelete = async (id, nome) => {
      if(!window.confirm(`Deseja excluir o cadastro de ${nome}?`)) return;
      try {
          await deleteDoc(doc(db, paths.users, id));
          await logAuditoria(userProfile, "Excluiu Cadete", { id, nome });
      } catch (error) {
          alert("Erro: " + error.message);
      }
  };

  // --- FUNÇÕES DE MÁSCARA ---
  const mascaraCPF = (value) => {
    let v = value.replace(/\D/g, "").slice(0, 11);
    if (v.length > 9) return v.replace(/^(\d{3})(\d{3})(\d{3})(\d{1,2}).*/, "$1.$2.$3-$4");
    if (v.length > 6) return v.replace(/^(\d{3})(\d{3})(\d+)/, "$1.$2.$3");
    if (v.length > 3) return v.replace(/^(\d{3})(\d+)/, "$1.$2");
    return v;
  };

  const mascaraRE = (value) => {
    let v = value.replace(/\D/g, "").slice(0, 7);
    if (v.length > 6) return v.replace(/^(\d{6})(\d{1}).*/, "$1-$2");
    return v;
  };

  // --- FUNÇÃO DE IMPORTAÇÃO CSV ---
  const handleImportCSV = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!activeTurmaId) {
        alert("Selecione uma Turma para importar.");
        if(fileInputRef.current) fileInputRef.current.value = "";
        return;
    }

    if (!window.confirm("Confirmar importação?")) return;

    setProcessing(true);
    setProgress("Lendo arquivo...");
    
    const reader = new FileReader();
    
    reader.onload = async (e) => {
        const text = e.target.result;
        const lines = text.split(/\r?\n/).filter(l => l.trim() !== "");
        
        let secondaryApp;
        try {
            secondaryApp = getApp("Secondary");
        } catch (err) {
            secondaryApp = initializeApp(auth.app.options, "Secondary");
        }
        const secondaryAuth = getAuth(secondaryApp);
        const turmaObj = turmas.find(t => t.id === activeTurmaId);

        let successCount = 0;
        let updatedCount = 0;
        let errorDetails = []; 
        const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

        for (let i = 1; i < lines.length; i++) {
            setProgress(`Processando ${i} de ${lines.length-1} alunos...`);
            const cols = lines[i].split(';');
            if (cols.length < 3) continue;

            const rawCpf = cols[0] ? cols[0].replace(/\D/g, '') : "";
            const rawRe = cols[1] ? cols[1].trim() : "";
            const rawNome = cols[2] ? cols[2].toUpperCase().trim() : "DESCONHECIDO";
            const rawQra = cols[3] ? cols[3].toUpperCase().trim() : "";
            
            let dataNasc = "";
            if (cols[4] && cols[4].includes('/')) {
                const parts = cols[4].trim().split('/');
                if (parts.length === 3) dataNasc = `${parts[2]}-${parts[1]}-${parts[0]}`;
            }

            let sexo = "M";
            if (cols[5] && cols[5].toUpperCase().includes("FEM")) sexo = "F";
            let vinculo = cols[6] && cols[6].toUpperCase().includes("CIVIL") ? "Civil" : "PM";
            let dataPosse = "";
            if (cols[7] && cols[7].includes('/')) {
                const parts = cols[7].trim().split('/');
                if (parts.length === 3) dataPosse = `${parts[2]}-${parts[1]}-${parts[0]}`;
            }

            if (!rawCpf) continue;

            try {
                await delay(800); 
                const email = `${rawCpf}@sistema.com`;
                const password = rawCpf; 
                let uidToUse = null;
                let isNewUser = false;
                
                try {
                    const userCredential = await createUserWithEmailAndPassword(secondaryAuth, email, password);
                    uidToUse = userCredential.user.uid;
                    isNewUser = true;
                    await signOut(secondaryAuth);
                } catch (authErr) {
                    if (authErr.code === 'auth/email-already-in-use') {
                        const q = query(collection(db, paths.users), where("email", "==", email));
                        const qs = await getDocs(q);
                        if (!qs.empty) uidToUse = qs.docs[0].id;
                        else {
                             try {
                                 const uc = await signInWithEmailAndPassword(secondaryAuth, email, password);
                                 uidToUse = uc.user.uid;
                                 isNewUser = true; 
                                 await signOut(secondaryAuth);
                             } catch (loginErr) {
                                 errorDetails.push(`Linha ${i+1}: Erro login.`);
                             }
                        }
                    }
                }

                if (uidToUse) {
                    const dadosParaSalvar = {
                        id: uidToUse,
                        email: email,
                        nome: rawNome,
                        qra: rawQra,
                        nome_guerra: rawQra,
                        re: rawRe,
                        matricula: rawRe,
                        cpf: rawCpf,
                        senha: rawCpf,
                        data_nascimento: dataNasc,
                        sexo: sexo, 
                        estado_civil: "Solteiro(a)", 
                        origem_ingresso: "Vestibular",
                        vinculo: vinculo,
                        data_posse: dataPosse,
                        nivel: 'cadete',
                        pelotao_id: '',
                        pelotao_nome: '',
                        companhia_id: '',
                        turma_id: activeTurmaId,
                        turma_nome: turmaObj?.nome || '',
                        acesso_liberado: true,
                        atualizado_em: new Date().toISOString()
                    };

                    if (isNewUser) dadosParaSalvar.criado_em = new Date().toISOString();
                    await setDoc(doc(db, paths.users, uidToUse), dadosParaSalvar, { merge: true });
                    if (isNewUser) successCount++; else updatedCount++;
                }

            } catch (err) {
                errorDetails.push(`Erro linha ${i+1}: ${err.message}`);
            }
        }
        setProcessing(false);
        alert(`Fim da Importação.\nNovos: ${successCount}\nAtualizados: ${updatedCount}`);
    };
    reader.readAsText(file);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6 animate-fade-in bg-gray-50 min-h-screen p-6">
        <header className="flex flex-col md:flex-row justify-between items-center border-b border-gray-200 pb-4 gap-4">
            <div>
                <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                    <UserIcon /> Gerenciamento de Cadetes
                </h1>
                <p className="text-sm text-gray-500 mt-1">Cadastro de dados pessoais e administrativos.</p>
            </div>
            <div className="flex gap-2 items-center">
                 {processing && <span className="text-sm text-blue-600 font-bold animate-pulse mr-2">{progress}</span>}
                 <input type="file" accept=".csv" ref={fileInputRef} style={{display: 'none'}} onChange={handleImportCSV} />
                 <Button onClick={() => fileInputRef.current && fileInputRef.current.click()} variant="outline" className="text-sm flex items-center gap-2 border-gray-400 text-gray-700" disabled={processing}><UploadIcon /> Importar CSV</Button>
                 <Button onClick={() => handleOpenModal()} variant="success" className="text-sm flex items-center gap-2"><PlusIcon /> Novo Cadete</Button>
            </div>
        </header>

        {/* Filtros */}
        <div className="space-y-4">
            <div className="flex gap-2 overflow-x-auto pb-1">
                {turmas.map(t => (
                    <button key={t.id} onClick={() => setActiveTurmaId(t.id)} className={`px-4 py-2 rounded-md text-sm font-bold whitespace-nowrap transition-colors border ${activeTurmaId === t.id ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-100'}`}>{t.nome}</button>
                ))}
            </div>

            {activeTurmaId && (
                <div className="flex gap-2 overflow-x-auto pb-1 animate-fade-in">
                    <button onClick={() => setActiveCiaId('')} className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${!activeCiaId ? 'bg-blue-100 text-blue-800 border-blue-300' : 'bg-white text-gray-500 border-gray-200'}`}>Todas</button>
                    {filteredCias.map(c => (
                        <button key={c.id} onClick={() => setActiveCiaId(c.id)} className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${activeCiaId === c.id ? 'bg-blue-100 text-blue-800 border-blue-300' : 'bg-white text-gray-500 border-gray-200'}`}>{c.nome}</button>
                    ))}
                </div>
            )}
            
            <div className="flex items-center bg-white p-2 rounded-lg border border-gray-200 shadow-sm max-w-sm">
                <SearchIcon />
                <input className="ml-2 w-full outline-none text-sm bg-transparent" placeholder="Pesquisar por Nome, RE ou CPF..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            </div>
        </div>

        {/* Tabela */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex justify-between items-center">
                <h3 className="font-bold text-gray-700 text-sm">Listagem Geral</h3>
                <span className="text-xs bg-gray-200 px-2 py-1 rounded text-gray-600 font-medium">{displayCadetes.length} Registros</span>
            </div>
            <table className="min-w-full text-sm">
                <thead className="bg-white text-gray-500 border-b border-gray-100">
                    <tr>
                        <th className="p-3 text-left w-24">RE / Matrícula</th>
                        <th className="p-3 text-left">Nome de Guerra</th>
                        <th className="p-3 text-left">Nome Completo</th>
                        <th className="p-3 text-center w-32">Nível</th>
                        <th className="p-3 text-center w-32">Pelotão</th>
                        <th className="p-3 text-right w-24">Ações</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                    {displayCadetes.map(c => (
                        <tr key={c.id} className="hover:bg-blue-50/50 transition-colors">
                            <td className="p-3 text-gray-600 font-mono font-medium">{c.re || c.matricula || '-'}</td>
                            <td className="p-3 font-bold text-gray-800">{c.qra || c.nome_guerra || '-'}</td>
                            <td className="p-3 text-gray-600">{c.nome}</td>
                            <td className="p-3 text-center text-xs text-gray-500 uppercase">{c.nivel}</td>
                            <td className="p-3 text-center">{c.pelotao_nome ? <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded text-xs border border-green-100 font-medium">{c.pelotao_nome}</span> : <span className="text-gray-400 italic text-xs">Sem Pelotão</span>}</td>
                            <td className="p-3 text-right">
                                <div className="flex justify-end gap-1">
                                    <button onClick={() => handleOpenModal(c)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded border border-transparent hover:border-blue-100"><EditIcon /></button>
                                    <button onClick={() => handleDelete(c.id, c.nome)} className="p-1.5 text-red-600 hover:bg-red-50 rounded border border-transparent hover:border-red-100"><TrashIcon /></button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>

        {/* Modal */}
        <Modal 
            show={showModal} 
            onClose={() => setShowModal(false)} 
            title={isEditing ? "Editar Cadastro" : "Novo Cadastro"} 
            size="6xl"
        >
            <form onSubmit={handleSave} className="space-y-6">
                
                <div className="bg-gray-50 p-3 rounded border border-gray-200">
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Vínculo Acadêmico (Turma)</label>
                    <select className="w-full border p-2 rounded text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none" value={formData.turma_id} onChange={e => setFormData({...formData, turma_id: e.target.value})} required>
                        <option value="">Selecione a Turma...</option>
                        {turmas.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
                    </select>
                </div>

                <div>
                    <h4 className="text-sm font-bold text-gray-700 border-b pb-1 mb-3">Identificação</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                         <div>
                             <label className="block text-xs font-medium text-gray-600 mb-1">CPF (Login)</label>
                             <input 
                                className="w-full border p-2 rounded text-sm disabled:bg-gray-100" 
                                value={formData.cpf} 
                                onChange={e => setFormData({ ...formData, cpf: mascaraCPF(e.target.value) })} 
                                disabled={isEditing} 
                                placeholder="000.000.000-00" 
                                required 
                            />
                         </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">RE / Matrícula</label>
                            <input 
                                className="w-full border p-2 rounded text-sm" 
                                value={formData.re} 
                                onChange={e => setFormData({ ...formData, re: mascaraRE(e.target.value) })} 
                                placeholder="000000-0"
                                required 
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                        <div><label className="block text-xs font-medium text-gray-600 mb-1">Nome Completo</label><input className="w-full border p-2 rounded text-sm uppercase" value={formData.nome} onChange={e=>setFormData({...formData, nome: e.target.value})} required /></div>
                        <div><label className="block text-xs font-medium text-gray-600 mb-1">Nome de Guerra (QRA)</label><input className="w-full border p-2 rounded text-sm uppercase" value={formData.qra} onChange={e=>setFormData({...formData, qra: e.target.value})} required /></div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                        <div><label className="block text-xs font-medium text-gray-600 mb-1">Data Nasc.</label><input type="date" className="w-full border p-2 rounded text-sm" value={formData.data_nascimento} onChange={e=>setFormData({...formData, data_nascimento: e.target.value})} /></div>
                        <div><label className="block text-xs font-medium text-gray-600 mb-1">Sexo</label><select className="w-full border p-2 rounded text-sm bg-white" value={formData.sexo} onChange={e=>setFormData({...formData, sexo: e.target.value})}><option value="M">Masculino</option><option value="F">Feminino</option></select></div>
                         <div><label className="block text-xs font-medium text-gray-600 mb-1">Vínculo</label><select className="w-full border p-2 rounded text-sm bg-white" value={formData.vinculo} onChange={e=>setFormData({...formData, vinculo: e.target.value})}><option value="PM">Militar (PM)</option><option value="Civil">Civil</option></select></div>
                        <div><label className="block text-xs font-medium text-gray-600 mb-1">Posse (PM)</label><input type="date" className="w-full border p-2 rounded text-sm" value={formData.data_posse} onChange={e=>setFormData({...formData, data_posse: e.target.value})} /></div>
                    </div>
                </div>

                <div>
                    <h4 className="text-sm font-bold text-gray-700 border-b pb-1 mb-3">Antiguidade (Classificação)</h4>
                    <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                        <div><label className="block text-[10px] font-medium text-gray-500 mb-1">Concurso</label><input type="number" className="w-full border p-1.5 rounded text-sm" value={formData.antiguidade_concurso} onChange={e=>setFormData({...formData, antiguidade_concurso: e.target.value})} /></div>
                        <div><label className="block text-[10px] font-medium text-gray-500 mb-1">1º CFO</label><input type="number" className="w-full border p-1.5 rounded text-sm" value={formData.antiguidade_1cfo} onChange={e=>setFormData({...formData, antiguidade_1cfo: e.target.value})} /></div>
                        <div><label className="block text-[10px] font-medium text-gray-500 mb-1">2º CFO</label><input type="number" className="w-full border p-1.5 rounded text-sm" value={formData.antiguidade_2cfo} onChange={e=>setFormData({...formData, antiguidade_2cfo: e.target.value})} /></div>
                        <div><label className="block text-[10px] font-medium text-gray-500 mb-1">3º CFO</label><input type="number" className="w-full border p-1.5 rounded text-sm" value={formData.antiguidade_3cfo} onChange={e=>setFormData({...formData, antiguidade_3cfo: e.target.value})} /></div>
                        <div><label className="block text-[10px] font-medium text-gray-500 mb-1">4º CFO</label><input type="number" className="w-full border p-1.5 rounded text-sm" value={formData.antiguidade_4cfo} onChange={e=>setFormData({...formData, antiguidade_4cfo: e.target.value})} /></div>
                    </div>
                </div>

                {/* --- BLOCO DE 3 COLUNAS --- */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    
                    {/* 1. Coluna: Nível do Usuário */}
                    <div className="bg-blue-50 p-3 rounded border border-blue-200 h-full flex flex-col justify-center">
                        <label className="block text-xs font-bold text-blue-800 uppercase mb-1">Tipo de Perfil</label>
                        <select 
                            className="w-full border p-1.5 rounded text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none" 
                            value={formData.nivel} 
                            onChange={e => setFormData({...formData, nivel: e.target.value})}
                        >
                            <option value="cadete">Cadete (Padrão)</option>
                            <option value="cadete_academico">Cadete Acadêmico</option>
                            <option value="escalante">Escalante</option> 
                            <option value="aspirante">Aspirante</option>
                        </select>
                    </div>

                    {/* 2. Coluna: Resetar Senha para CPF */}
                    <div className="bg-orange-50 p-3 rounded border border-orange-200 h-full flex flex-col justify-center">
                         <label className="block text-xs font-bold text-orange-800 uppercase mb-1">Senha de Acesso</label>
                         
                         {/* EXIBIÇÃO DA SENHA ATUAL */}
                         <div className="mb-2 w-full text-center">
                            <span className="text-[10px] text-orange-600 uppercase font-bold">Senha Atual</span>
                            <div className="bg-white border border-orange-200 rounded p-1 text-center font-mono text-sm text-orange-900 font-bold select-all">
                                {formData.senha || '-'}
                            </div>
                         </div>

                         <button 
                            type="button"
                            onClick={handleResetSenhaParaCPF}
                            disabled={!isEditing || processing}
                            className="w-full bg-orange-100 hover:bg-orange-200 text-orange-800 border border-orange-300 text-xs font-bold py-2 px-3 rounded flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                         >
                            <KeyIcon />
                            Resetar Para CPF
                         </button>
                         <p className="text-[9px] text-orange-600 mt-1 leading-tight text-center">* Define a senha igual ao CPF (Apenas Banco de Dados).</p>
                    </div>

                    {/* 3. Coluna: Acesso ao Sistema */}
                    <div className="bg-gray-50 p-3 rounded border border-gray-200 h-full flex flex-col justify-center">
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <h4 className="text-xs font-bold text-gray-700 uppercase">Login</h4>
                                <p className="text-[9px] text-gray-500">Liberar entrada</p>
                            </div>
                            <button 
                                type="button" 
                                onClick={() => setFormData({...formData, acesso_liberado: !formData.acesso_liberado})} 
                                className={`px-3 py-1.5 rounded text-xs font-bold border transition-colors ${formData.acesso_liberado ? 'bg-green-100 text-green-700 border-green-300' : 'bg-red-100 text-red-700 border-red-300'}`}
                            >
                                {formData.acesso_liberado ? 'LIBERADO' : 'BLOQUEADO'}
                            </button>
                        </div>

                        {/* Nova chave de permissão para o Livro de Ordens */}
                        <div className="pt-3 border-t border-gray-200">
                            <label className="flex items-center gap-2 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={formData.canPostLivroDeOrdem || false}
                                    onChange={(e) => setFormData({ ...formData, canPostLivroDeOrdem: e.target.checked })}
                                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 border-gray-300"
                                />
                                <span className="font-bold text-gray-700 text-[11px] uppercase">
                                    Missão Livro de Ordem
                                </span>
                            </label>
                            <p className="text-[9px] text-gray-500 ml-6 mt-1 leading-tight">Permite lançar novas ordens no Mural</p>
                        </div>
                    </div>
                </div>

                {/* --- NOVO: CHECKBOX LÍDER EO --- */}
                <div className="mt-4 p-3 border border-blue-200 rounded bg-blue-50/50">
                    <label className="flex items-center gap-3 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500 border-gray-300"
                            checked={formData.isLiderEO || false}
                            onChange={(e) => setFormData({ ...formData, isLiderEO: e.target.checked })}
                        />
                        <span className="font-bold text-blue-800 text-sm">
                            Cadete Líder EO (Permissão para criar horários de revista)
                        </span>
                    </label>
                </div>
                <div className="flex items-center space-x-2 mt-4 p-2 bg-gray-50 rounded-md border border-gray-200">
  <input
    type="checkbox"
    id="canPostLivroDeOrdem"
    name="canPostLivroDeOrdem"
    checked={formData.canPostLivroDeOrdem || false}
    onChange={(e) => setFormData({ ...formData, canPostLivroDeOrdem: e.target.checked })}
    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
  />
  <label htmlFor="canPostLivroDeOrdem" className="text-sm font-semibold text-gray-700 cursor-pointer">
    Missão Livro de Ordem (Permite lançar ordens no Mural)
  </label>
</div>

                <div className="flex justify-end pt-2 border-t gap-2">
                    <Button variant="secondary" onClick={() => setShowModal(false)} type="button">Cancelar</Button>
                    <Button type="submit" variant="primary" disabled={processing}>{processing ? "Salvando..." : "Salvar Cadastro"}</Button>
                </div>
            </form>
        </Modal>
    </div>
  );
};